/**
 * CSV Security Utilities
 * 
 * Provides secure CSV export functionality with comprehensive protection against:
 * - CSV Injection (Formula Injection) attacks
 * - Malformed CSV files from improper escaping
 * - RFC 4180 compliance violations
 * 
 * @author AI Assistant
 * @version 1.0.0
 */

/**
 * Interface for CSV export configuration
 */
export interface CsvExportConfig {
  /** Filename for the CSV export (without .csv extension) */
  filename: string;
  /** Headers for the CSV file */
  headers: string[];
  /** Whether to sanitize dangerous formula prefixes */
  preventInjection?: boolean;
  /** Custom delimiter (defaults to comma) */
  delimiter?: string;
  /** Whether to force quote all fields */
  forceQuoteAll?: boolean;
}

/**
 * Interface for secure CSV field processing
 */
export interface CsvFieldOptions {
  /** Whether to prevent CSV injection */
  preventInjection?: boolean;
  /** Whether to force quote this field */
  forceQuote?: boolean;
}

/**
 * Dangerous formula prefixes that could cause CSV injection
 */
const DANGEROUS_PREFIXES = ['=', '+', '-', '@'];

/**
 * Characters that require field quoting according to RFC 4180
 */
const QUOTE_REQUIRED_CHARS = [',', '"', '\n', '\r'];

/**
 * Detects if a string contains dangerous formula prefixes
 * @param value - The string to check
 * @returns True if the value contains dangerous prefixes
 */
export function hasDangerousPrefix(value: string): boolean {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }
  
  const trimmed = value.trim();
  return DANGEROUS_PREFIXES.some(prefix => trimmed.startsWith(prefix));
}

/**
 * Sanitizes dangerous formula prefixes by adding a safe prefix
 * @param value - The string to sanitize
 * @returns Sanitized string with safe prefix if needed
 */
export function sanitizeFormulaPrefix(value: string): string {
  if (typeof value !== 'string') {
    return String(value);
  }
  
  if (hasDangerousPrefix(value)) {
    // Add a single quote prefix to neutralize formula interpretation
    return `'${value}`;
  }
  
  return value;
}

/**
 * Determines if a field needs to be quoted according to RFC 4180
 * @param value - The field value
 * @param delimiter - The CSV delimiter
 * @returns True if the field needs quoting
 */
export function needsQuoting(value: string, delimiter: string = ','): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  
  // Check for delimiter
  if (value.includes(delimiter)) {
    return true;
  }
  
  // Check for other characters that require quoting
  return QUOTE_REQUIRED_CHARS.some(char => value.includes(char));
}

/**
 * Escapes double quotes within a field by doubling them
 * @param value - The field value
 * @returns Value with escaped quotes
 */
export function escapeQuotes(value: string): string {
  if (typeof value !== 'string') {
    return String(value);
  }
  
  return value.replace(/"/g, '""');
}

/**
 * Removes or replaces dangerous control characters
 * @param value - The string to sanitize
 * @returns Sanitized string
 */
export function sanitizeControlCharacters(value: string): string {
  if (typeof value !== 'string') {
    return String(value);
  }
  
  // Remove most control characters except tab, newline, and carriage return
  // which are handled by proper CSV quoting
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
}

/**
 * Securely escapes a single CSV field according to RFC 4180 with injection prevention
 * @param value - The field value to escape
 * @param options - Configuration options for field processing
 * @returns Properly escaped CSV field
 */
export function escapeCSVField(
  value: unknown, 
  options: CsvFieldOptions = {}
): string {
  const { preventInjection = true, forceQuote = false } = options;
  if (value === null || value === undefined) return '';
  let stringValue = String(value);
  // Early optimization: if empty string, return immediately
  if (stringValue === '') return '';
  // Sanitize control characters
  stringValue = sanitizeControlCharacters(stringValue);
  // Check if quoting is needed before injection prevention
  const needsQuotes = forceQuote || needsQuoting(stringValue);
  // Prevent CSV injection if enabled
  if (preventInjection && hasDangerousPrefix(stringValue)) {
    stringValue = `'${stringValue}`;
  }
  if (needsQuotes) {
    // Escape internal quotes and wrap in quotes
    const escapedValue = stringValue.replace(/"/g, '""');
    return `"${escapedValue}"`;
  }
  return stringValue;
}

/**
 * Converts an array of values to a properly escaped CSV row
 * @param row - Array of field values
 * @param options - Configuration options
 * @returns Escaped CSV row string
 */
export function createCSVRow(
  row: unknown[], 
  options: CsvFieldOptions & { delimiter?: string } = {}
): string {
  const { delimiter = ',', ...fieldOptions } = options;
  
  return row
    .map(field => escapeCSVField(field, fieldOptions))
    .join(delimiter);
}

/**
 * Converts 2D array data to complete CSV content with headers
 * @param data - 2D array of data
 * @param config - CSV export configuration
 * @returns Complete CSV content string
 */
export function createCSVContent(
  data: unknown[][], 
  config: CsvExportConfig
): string {
  const { 
    headers, 
    preventInjection = true, 
    delimiter = ',', 
    forceQuoteAll = false 
  } = config;
  
  const fieldOptions: CsvFieldOptions = {
    preventInjection,
    forceQuote: forceQuoteAll
  };
  
  const rowOptions = { ...fieldOptions, delimiter };
  
  // Create header row
  const headerRow = createCSVRow(headers, rowOptions);
  
  // Create data rows
  const dataRows = data.map(row => createCSVRow(row, rowOptions));
  
  // Combine all rows
  return [headerRow, ...dataRows].join('\n');
}

/**
 * Triggers download of CSV content as a file
 * @param csvContent - The CSV content string
 * @param filename - Filename without extension
 * @returns Promise that resolves when download is initiated
 */
export function downloadCSV(csvContent: string, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      // Create blob with proper MIME type and UTF-8 BOM for Excel compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { 
        type: 'text/csv;charset=utf-8;' 
      });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
      
      // Hide link and trigger download
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * High-level secure CSV export function
 * @param data - 2D array of data to export
 * @param config - Export configuration
 * @returns Promise that resolves when export is complete
 */
export async function exportSecureCSV(
  data: unknown[][], 
  config: CsvExportConfig
): Promise<void> {
  try {
    const csvContent = createCSVContent(data, config);
    await downloadCSV(csvContent, config.filename);
  } catch (error) {
    throw new Error(`CSV export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Utility function to convert object array to 2D array for CSV export
 * @param objects - Array of objects to convert
 * @param keys - Keys to extract (in order)
 * @returns 2D array suitable for CSV export
 */
export function objectArrayToCSVData<T extends Record<string, unknown>>(
  objects: T[], 
  keys: readonly (keyof T)[]
): unknown[][] {
  return objects.map(obj => keys.map(key => obj[key]));
}

/**
 * Validates CSV content for security issues
 * @param csvContent - The CSV content to validate
 * @returns Array of security warnings
 */
export function validateCSVSecurity(csvContent: string): string[] {
  const warnings: string[] = [];
  const lines = csvContent.split('\n');
  
  lines.forEach((line, index) => {
    // Parse the line properly according to RFC 4180
    const fields: string[] = [];
    let currentField = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          // Escaped quote inside quoted field
          currentField += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        fields.push(currentField);
        currentField = '';
      } else {
        // Regular character
        currentField += char;
      }
    }
    
    // Add the last field
    fields.push(currentField);
    
    // Check each field for dangerous content
    fields.forEach((field, fieldIndex) => {
      const trimmedField = field.trim();
      
      // Check if the field starts with a dangerous prefix
      // but is not properly escaped with a single quote prefix
      if (hasDangerousPrefix(trimmedField) && 
          !trimmedField.startsWith("'") && 
          !trimmedField.startsWith("\"'")) {
        warnings.push(
          `Line ${index + 1}, Field ${fieldIndex + 1}: Potential CSV injection detected: "${field}"`
        );
      }
    });
  });
  
  return warnings;
} 